import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, CheckCircle2, AlertCircle } from "lucide-react";

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [stage, setStage] = useState("checking"); // checking | ready | invalid | done
  const [invite, setInvite] = useState(null);
  const [reason, setReason] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setStage("invalid");
      setReason("Kein Token im Link gefunden.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/invitations/${token}`);
        setInvite(data);
        setStage("ready");
      } catch (e) {
        setReason(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        setStage("invalid");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Passwort muss mindestens 6 Zeichen lang sein.");
      return;
    }
    if (password !== confirm) {
      setError("Passwörter stimmen nicht überein.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/invitations/accept", { token, password });
      setUser(data);
      setStage("done");
      const target =
        data?.role === "client" ? "/mein-bereich"
        : data?.role === "participant" ? "/weiterbildung/mein-bereich"
        : "/dashboard";
      setTimeout(() => navigate(target, { replace: true }), 1200);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const continueWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 py-10">
      <div className="w-full max-w-md bg-white border border-slate-200 p-10">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-overline text-slate-500 hover:text-slate-900 mb-6"
        >
          <ArrowLeft size={12} strokeWidth={1.5} />
          Zur Anmeldung
        </Link>

        <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Einladung</div>
        <h1 className="font-heading text-4xl font-light tracking-tighter text-slate-900 mt-2">
          Konto aktivieren.
        </h1>

        {stage === "checking" && (
          <div className="mt-8 text-sm text-slate-500" data-testid="invite-checking">
            Einladung wird geprüft…
          </div>
        )}

        {stage === "invalid" && (
          <div
            className="mt-8 flex items-start gap-3 border-l-2 border-red-600 pl-4 py-2"
            data-testid="invite-invalid"
          >
            <AlertCircle size={18} strokeWidth={1.5} className="text-red-600 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-slate-900">Einladung ungültig</div>
              <p className="text-sm text-slate-600 leading-relaxed mt-1">{reason}</p>
              <p className="text-xs text-slate-500 mt-3">
                Bitte beim Admin nach einem neuen Einladungslink fragen.
              </p>
            </div>
          </div>
        )}

        {stage === "ready" && invite && (
          <>
            <div className="mt-8 border-l-2 border-blue-700 pl-4 py-1">
              <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Eingeladen für</div>
              <div className="font-heading text-2xl font-light tracking-tight text-slate-900 mt-1" data-testid="invite-name">
                {invite.name}
              </div>
              <div className="text-sm text-slate-500 font-mono mt-1" data-testid="invite-email">
                {invite.email}
              </div>
            </div>

            <form onSubmit={submit} className="mt-8 space-y-6" data-testid="invite-form">
              <p className="text-sm text-slate-500">
                Lege jetzt dein persönliches Passwort fest, um dein Konto zu aktivieren.
              </p>

              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">
                  Neues Passwort
                </label>
                <input
                  data-testid="invite-password-input"
                  type="password"
                  className="input-tech"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); if (error) setError(""); }}
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">
                  Passwort bestätigen
                </label>
                <input
                  data-testid="invite-confirm-input"
                  type="password"
                  className="input-tech"
                  value={confirm}
                  onChange={(e) => { setConfirm(e.target.value); if (error) setError(""); }}
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>
              {error && (
                <div data-testid="invite-error" className="text-sm text-red-600 border-l-2 border-red-600 pl-3 py-1">
                  {error}
                </div>
              )}
              <button
                data-testid="invite-submit-button"
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 text-white py-4 text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                {loading ? "Wird aktiviert…" : "Konto aktivieren & anmelden"}
              </button>
            </form>

            <div className="flex items-center gap-4 my-8">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs font-bold uppercase tracking-overline text-slate-400">oder</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <button
              data-testid="invite-google-button"
              type="button"
              onClick={continueWithGoogle}
              className="w-full border border-slate-900 text-slate-900 py-4 text-sm font-medium hover:bg-slate-900 hover:text-white transition-colors flex items-center justify-center gap-3"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.20455c0-.638-.0573-1.25182-.1636-1.84091H9v3.48136h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.8741 2.6836-6.61545z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.806.54-1.8368.8595-3.0477.8595-2.3445 0-4.3282-1.5832-5.036-3.7104H.957v2.3318C2.4382 15.9831 5.4818 18 9 18z" fill="#34A853"/>
                <path d="M3.964 10.71c-.18-.54-.2823-1.1168-.2823-1.71s.1023-1.17.2823-1.71V4.95818H.957C.3477 6.1731 0 7.5477 0 9c0 1.4523.3477 2.8268.957 4.0418L3.964 10.71z" fill="#FBBC05"/>
                <path d="M9 3.57955c1.3214 0 2.5077.45409 3.4405 1.34591l2.5813-2.58131C13.4632.891818 11.4259 0 9 0 5.4818 0 2.4382 2.01682.957 4.95818L3.964 7.29c.7077-2.12727 2.6914-3.71045 5.036-3.71045z" fill="#EA4335"/>
              </svg>
              Direkt mit Google fortfahren
            </button>
            <p className="text-xs text-slate-500 mt-3 leading-relaxed">
              Deine Google-E-Mail muss <span className="font-mono">{invite.email}</span> sein, sonst klappt
              die Anmeldung nicht.
            </p>
          </>
        )}

        {stage === "done" && (
          <div className="mt-8 flex items-start gap-3 border-l-2 border-emerald-600 pl-4 py-2" data-testid="invite-done">
            <CheckCircle2 size={18} strokeWidth={1.5} className="text-emerald-600 shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700">
              Konto aktiviert. Du wirst zum Dashboard weitergeleitet…
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
